import { memo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import WarningBadge from './WarningBadge';
import {
  CardTitle,
  ChartLoadingState,
  ChartTooltipBox,
  EmptyChartState,
  LegendPill,
  OVERVIEW_CARD_SX,
} from './OverviewCard';
import { formatCompactCount, formatCount } from 'src/utils/formatNumber.util';

const CHART_HEIGHT = 168;

const renderTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0].payload;

  return (
    <ChartTooltipBox>
      {item.label}: {formatCount(item.value)}
    </ChartTooltipBox>
  );
};

const StatusBarChart = ({
  title,
  subtitle,
  data = [],
  titleSize = 'md',
  onItemClick,
  isLoading,
  emptyMessage = 'No data yet',
  warningCount = 0,
  warningType = 'Issues',
  onWarningClick,
  sx,
}) => (
  <Stack
    gap={1}
    sx={{
      ...OVERVIEW_CARD_SX,
      minWidth: 0,
      position: 'relative',
      ...(warningCount > 0 && { boxShadow: '0 0 0 2px rgba(255, 167, 38, 0.4)' }),
      ...sx,
    }}>
    <WarningBadge count={warningCount} onClick={onWarningClick} type={warningType} />
    <CardTitle title={title} subtitle={subtitle} size={titleSize} />

    {isLoading ? (
      <ChartLoadingState height={CHART_HEIGHT} />
    ) : !data.length ? (
      <EmptyChartState height={CHART_HEIGHT} message={emptyMessage} />
    ) : (
      <>
        <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.16)"
                vertical={false}
              />

              <XAxis
                dataKey="label"
                interval={0}
                stroke="#D1D5DB"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: '#D1D5DB' }}
              />

              <YAxis
                allowDecimals={false}
                width={32}
                stroke="#D1D5DB"
                tickLine={false}
                axisLine={false}
                tickFormatter={formatCompactCount}
                tick={{ fontSize: 10, fill: '#D1D5DB' }}
              />

              <Tooltip
                isAnimationActive={false}
                cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                content={renderTooltip}
              />

              <Bar
                dataKey="value"
                barSize={34}
                radius={[4, 4, 0, 0]}
                cursor={onItemClick ? 'pointer' : 'default'}
                onClick={entry => onItemClick?.(entry.payload)}>
                {data.map(entry => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="center">
          {data.map(entry => (
            <LegendPill
              key={entry.name}
              color={entry.color}
              label={entry.label}
              value={entry.value}
              onClick={onItemClick ? () => onItemClick(entry) : undefined}
            />
          ))}
        </Stack>
      </>
    )}
  </Stack>
);

export default memo(StatusBarChart);
